import React, { useState } from "react";
import { BRANDS } from "../data/products";
import "../styles/productCard.css";

const fmt = (n) => `MK ${n.toLocaleString()}`;

function ApparelOptions({ product, slideIdx, setSlideIdx, selSize, setSelSize, sizeError }) {
  return (
    <>
      <div className="product-card__swatches">
        {product.colors.map((c, i) => (
          <button
            key={c.name}
            title={c.name}
            onClick={() => setSlideIdx(i)}
            className="product-card__swatch"
            style={{
              background:    c.hex,
              outline:       slideIdx === i ? `2px solid ${c.hex}` : "none",
              outlineOffset: 2,
            }}
          />
        ))}
      </div>
      <div className={`product-card__sizes ${sizeError ? "product-card__sizes--error" : ""}`}>
        {product.sizes.map((sz) => (
          <button
            key={sz}
            onClick={() => setSelSize(sz)}
            className={`product-card__size-btn ${selSize === sz ? "product-card__size-btn--active" : ""}`}
          >
            {sz}
          </button>
        ))}
      </div>
      {sizeError && (
        <p className="product-card__size-msg">Please select a size</p>
      )}
    </>
  );
}

// compact=true    → small card, no inline pickers, whole card click opens modal
// onCardClick     → if provided, whole card click opens modal
// onImageClick    → if provided, only the image click opens modal (standard cards)
export default function ProductCard({ product, onAddToCart, compact = false, onCardClick, onImageClick }) {
  const isApparel  = product.type === "apparel";
  const brand      = BRANDS[product.brand];
  const useModal   = compact || !!onCardClick;

  const images = isApparel
    ? product.colors.map((c) => c.image)
    : (product.images || [product.image]);

  const [slideIdx,    setSlideIdxRaw] = useState(0);
  const [selSize,     setSelSize]     = useState(null);
  const [added,       setAdded]       = useState(false);
  const [sizeError,   setSizeError]   = useState(false);
  const [touchStartX, setTouchStartX] = useState(null);

  const selColor = isApparel ? product.colors[slideIdx] : null;

  const setSlideIdx = (i) =>
    setSlideIdxRaw(Math.max(0, Math.min(i, images.length - 1)));

  const onTouchStart = (e) => setTouchStartX(e.targetTouches[0].clientX);
  const onTouchEnd   = (e) => {
    if (touchStartX === null) return;
    const diff = touchStartX - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) setSlideIdx(slideIdx + (diff > 0 ? 1 : -1));
    setTouchStartX(null);
  };

  const handleAdd = (e) => {
    if (isApparel && !selSize) {
      setSizeError(true);
      setTimeout(() => setSizeError(false), 1800);
      return;
    }
    onAddToCart({
      ...product,
      ...(isApparel && {
        selColor:    selColor.name,
        selColorHex: selColor.hex,
        selImage:    selColor.image,
        selSize,
      }),
    });
    const rect = e.currentTarget.getBoundingClientRect();
    window.dispatchEvent(new CustomEvent("gvs:add-to-cart", {
      detail: { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 },
    }));
    setAdded(true);
    setTimeout(() => setAdded(false), 1400);
  };

  const handleCardClick = () => {
    if (useModal && onCardClick) onCardClick(product);
  };

  return (
    <div
      className={`product-card ${compact ? "product-card--compact" : ""}`}
      onClick={useModal ? handleCardClick : undefined}
    >
      {brand && (
        <span className="product-card__tag" style={{ background: brand.tagBg, color: brand.tagColor }}>
          {brand.name}
        </span>
      )}

      <div
        className="product-card__img-box"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onClick={!useModal && onImageClick ? () => onImageClick(product) : undefined}
        style={!useModal && onImageClick ? { cursor: "pointer" } : undefined}
      >
        <img
          key={images[slideIdx]}
          className="product-card__img"
          src={images[slideIdx]}
          alt={`${product.name}${selColor ? ` in ${selColor.name}` : ""}`}
        />

        {compact && (
          <div className="product-card__compact-overlay">View</div>
        )}

        {images.length > 1 && (
          <div className="product-card__dots">
            {images.map((_, i) => (
              <button
                key={i}
                className={`product-card__dot ${i === slideIdx ? "product-card__dot--active" : ""}`}
                onClick={(e) => { e.stopPropagation(); setSlideIdx(i); }}
                aria-label={`Image ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {compact ? (
        <div className="product-card__compact-body">
          <p className="product-card__name">{product.name}</p>
          <p className="product-card__price">{fmt(product.price)}</p>
        </div>
      ) : (
        <div className="product-card__body">
          <p className="product-card__collection">{product.collection}</p>
          <h3 className="product-card__name">{product.name}</h3>

          {product.description && (
            <p style={{
              fontSize: 11,
              color: "#999",
              margin: "4px 0 6px",
              lineHeight: 1.5,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}>
              {product.description}
            </p>
          )}

          {isApparel && !useModal && (
            <ApparelOptions
              product={product}
              slideIdx={slideIdx}
              setSlideIdx={setSlideIdx}
              selSize={selSize}
              setSelSize={setSelSize}
              sizeError={sizeError}
            />
          )}

          <div className="product-card__footer">
            <span className="product-card__price">{fmt(product.price)}</span>
            {useModal ? (
              <button className="product-card__add-btn" onClick={(e) => { e.stopPropagation(); onCardClick(product); }}>
                View
              </button>
            ) : (
              <button
                onClick={(e) => handleAdd(e)}
                className={`product-card__add-btn ${added ? "product-card__add-btn--done" : ""}`}
              >
                {added ? "✓ Added" : "Add to Bag"}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
